import { useQuery } from '@apollo/client/react';
import { graphql } from '@/gql';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { RangeDatePicker } from '@/components/ui/DatePickers';
import { useUrlState } from '@/hooks/useUrlState';
import { consumptionReportUrlSchema } from './consumptionReportUrlState';

const ConsumptionReportDoc = graphql(/* GraphQL */ `
  query ConsumptionReport($dateFrom: String!, $dateTo: String!) {
    consumptionReport(dateFrom: $dateFrom, dateTo: $dateTo) {
      dateFrom
      dateTo
      rows {
        consumptionName
        unitLabel
        totalUnits
        lines {
          productId
          productName
          unitAmount
          qtySold
          unitsContributed
        }
      }
    }
  }
`);

export function ConsumptionReportPage() {
  const { state: url, patch } = useUrlState(consumptionReportUrlSchema);
  const dateFrom = url.from;
  const dateTo = url.to;

  const { data, loading, error } = useQuery(ConsumptionReportDoc, {
    variables: { dateFrom, dateTo },
    skip: !dateFrom || !dateTo,
  });

  const report = data?.consumptionReport;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Consumption report</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            See how many units of each product were sold so you know what to restock.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[18rem]">
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Period</span>
            <RangeDatePicker
              value={{ from: dateFrom, to: dateTo }}
              onChange={(next) => {
                patch({ from: next.from, to: next.to });
              }}
              ariaLabel="Consumption report period"
            />
          </div>
        </div>
      </div>

      {error && (
        <EmptyState title="Could not load report" description={error.message} />
      )}

      {!error && (
        <Card title={report ? `Results — ${report.dateFrom} to ${report.dateTo}` : 'Results'}>
          {loading && !report ? (
            <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
          ) : report && report.rows.length === 0 ? (
            <EmptyState
              title="No consumption data"
              description="No products with consumption settings were sold in this period."
            />
          ) : report ? (
            <div className="-mx-3 overflow-x-auto sm:mx-0">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Product / Group</th>
                    <th className="px-3 py-2 text-right">Qty sold</th>
                    <th className="px-3 py-2 text-right">Per unit</th>
                    <th className="px-3 py-2 text-right">Total units</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                  {report.rows.map((row) => (
                    <ReportGroup key={`${row.consumptionName}||${row.unitLabel}`} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}

interface ReportGroupProps {
  row: {
    consumptionName: string;
    unitLabel: string;
    totalUnits: number;
    lines: Array<{
      productId: string;
      productName: string;
      unitAmount: number;
      qtySold: number;
      unitsContributed: number;
    }>;
  };
}

function ReportGroup({ row }: ReportGroupProps) {
  const showSubRows = row.lines.length > 1;

  return (
    <>
      <tr className="bg-gray-50/80 font-semibold dark:bg-gray-800/50">
        <td className="px-3 py-3">{row.consumptionName}</td>
        <td className="px-3 py-3 text-right tabular-nums">
          {showSubRows ? '' : row.lines[0]?.qtySold ?? ''}
        </td>
        <td className="px-3 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400">
          {showSubRows ? '' : `${row.lines[0]?.unitAmount ?? ''} ${row.unitLabel}`}
        </td>
        <td className="px-3 py-3 text-right tabular-nums">
          {formatUnits(row.totalUnits)} {row.unitLabel}
        </td>
      </tr>
      {showSubRows &&
        row.lines.map((line) => (
          <tr
            key={line.productId}
            className="text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/40"
          >
            <td className="py-2 pl-8 pr-3 text-sm">{line.productName}</td>
            <td className="px-3 py-2 text-right tabular-nums text-sm">{line.qtySold}</td>
            <td className="px-3 py-2 text-right tabular-nums text-sm text-gray-400 dark:text-gray-500">
              {line.unitAmount} {row.unitLabel}
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-sm">
              {formatUnits(line.unitsContributed)} {row.unitLabel}
            </td>
          </tr>
        ))}
    </>
  );
}

function formatUnits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}
